import { useEffect, useState, useRef, useCallback } from 'react';
import { useTerminalStore } from '../../stores/terminal.store.js';

const BOOT_LINES = [
  { text: '[SYS] ARC TERMINAL BOOT SEQUENCE INITIATED',     type: 'sys' },
  { text: '[SYS] Loading core modules...',                   type: 'sys' },
  { text: '[SYS] Market data sources: Binance, CoinGecko, Birdeye', type: 'sys' },
  { text: '[SYS] Initializing UT Bot Engine v2.4...',        type: 'sys' },
  { text: '[OK]  ATR (Wilder smoothed): LOADED',             type: 'ok'  },
  { text: '[OK]  Trail stop calculator: ARMED',              type: 'ok'  },
  { text: '[OK]  Signal crossover detection: ACTIVE',        type: 'ok'  },
  { text: '[SYS] Loading TradingView chart renderer...',     type: 'sys' },
  { text: '[OK]  Lightweight Charts v4: INITIALIZED',        type: 'ok'  },
  { text: '[OK]  Candlestick + volume series: READY',        type: 'ok'  },
  { text: '[SYS] Calibrating HUD overlays...',               type: 'sys' },
  { text: '[OK]  Arc Reactor HUD: ONLINE',                   type: 'ok'  },
  { text: '[OK]  Holographic grid: RENDERED',                type: 'ok'  },
  { text: '[SYS] Zustand state hydrated from localStorage',  type: 'sys' },
  { text: '[SYS] LAUNCHING ARC TERMINAL...',                 type: 'launch' },
] as const;

const LINE_DELAY_MS = 110;

export function BootSequence() {
  const setBootComplete = useTerminalStore(s => s.setBootComplete);
  const [visibleCount, setVisibleCount] = useState(0);
  const [progress,     setProgress]     = useState(0);
  const [exiting,      setExiting]      = useState(false);
  const timeoutsRef = useRef<ReturnType<typeof setTimeout>[]>([]);

  const skip = useCallback(() => {
    timeoutsRef.current.forEach(clearTimeout);
    setBootComplete();
  }, [setBootComplete]);

  // Any key skips the intro
  useEffect(() => {
    const onKey = () => skip();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [skip]);

  useEffect(() => {
    const total = BOOT_LINES.length;
    const timeouts = timeoutsRef.current;

    for (let i = 0; i < total; i++) {
      const t = setTimeout(() => {
        setVisibleCount(i + 1);
        setProgress(Math.round(((i + 1) / total) * 100));
      }, i * LINE_DELAY_MS);
      timeouts.push(t);
    }

    // Exit after last line
    const exitT = setTimeout(() => {
      setExiting(true);
      setTimeout(() => setBootComplete(), 800);
    }, total * LINE_DELAY_MS + 300);
    timeouts.push(exitT);

    return () => timeouts.forEach(clearTimeout);
  }, [setBootComplete]);

  return (
    <div
      className={`
        fixed inset-0 z-50 bg-black flex flex-col items-center justify-center
        transition-opacity duration-700
        ${exiting ? 'opacity-0 pointer-events-none' : 'opacity-100'}
      `}
    >
      <button
        onClick={skip}
        autoFocus
        aria-label="Skip intro"
        className="absolute top-4 right-4 font-mono text-[11px] tracking-widest border border-arc/40 text-arc px-3 py-1.5 hover:bg-arc/10 transition-colors"
      >
        SKIP ▸
      </button>

      {/* Logo */}
      <div
        className="font-hud font-bold tracking-[10px] mb-8 text-arc"
        style={{
          fontSize: 'clamp(32px, 6vw, 56px)',
          textShadow: '0 0 30px var(--color-arc)',
        }}
      >
        A R C
      </div>
      <p className="font-mono text-[13px] tracking-wider mb-6 text-center px-4" style={{ color: 'var(--color-bright)' }}>
        Multi-chart crypto terminal with UT Bot signals and risk-free paper trading.
      </p>

      {/* Boot lines */}
      <div className="w-full max-w-xl px-4 font-mono text-[11px] flex flex-col gap-0.5 mb-4">
        {BOOT_LINES.slice(0, visibleCount).map((line, i) => (
          <div
            key={i}
            className={`
              ${line.type === 'ok'     ? 'text-buy'    : ''}
              ${line.type === 'sys'    ? 'text-arc'    : ''}
              ${line.type === 'launch' ? 'text-accent font-bold' : ''}
            `}
          >
            {line.text}
            {i === visibleCount - 1 && !exiting && (
              <span className="inline-block w-2 h-3 bg-arc ml-1 animate-blink" />
            )}
          </div>
        ))}
      </div>

      {/* Progress bar */}
      <div className="w-full max-w-xl px-4">
        <div className="h-0.5 bg-[#0a2040] w-full rounded overflow-hidden">
          <div
            className="h-full bg-arc transition-all duration-100"
            style={{ width: `${progress}%` }}
          />
        </div>
        <div className="flex justify-between font-mono text-[11px] text-muted mt-1">
          <span>LOADING MODULES · PRESS ANY KEY TO SKIP</span>
          <span>{progress}%</span>
        </div>
      </div>
    </div>
  );
}
